"use client";

import { ProductCategoriesListDocument } from "@/gql/graphql";
import { useChannel } from "@/context/channel";
import { executeGraphQL } from "@/lib/graphql";
import { defaultLocale, handleGraphqlLocale } from "@/lib/utils/util";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

type CategoryNode = {
	id: string;
	name: string;
	slug: string;
	translation?: { name?: string } | null;
	metadata?: { key: string; value: string }[];
	products?: { totalCount: number };
};

type CategoryEdge = { node: CategoryNode; image?: string };

const IMAGE_MAP: Record<string, string> = {
	cavitation: "/image/gykj/categories/cavitation-40k.jpg",
	"40k": "/image/gykj/categories/cavitation-40k.jpg",
	"80k": "/image/gykj/categories/cavitation-80k.jpg",
	slimming: "/image/gykj/categories/cavitation-80k.jpg",
	rf: "/image/gykj/categories/rf-therapy.jpg",
	tecar: "/image/gykj/categories/rf-therapy.jpg",
	"448k": "/image/gykj/categories/rf-therapy.jpg",
	therapy: "/image/gykj/categories/deep-care-448k.jpg",
	microneedle: "/image/gykj/categories/golden-microneedle.jpg",
	tattoo: "/image/gykj/categories/picosecond-laser.jpg",
	pico: "/image/gykj/categories/picosecond-laser.jpg",
	laser: "/image/gykj/categories/diode-laser-810nm.jpg",
	"810": "/image/gykj/categories/diode-laser-810nm.jpg",
	diode: "/image/gykj/categories/diode-laser-810nm.jpg",
	hair: "/image/gykj/categories/opt-hair-removal.jpg",
	opt: "/image/gykj/categories/opt-hair-removal.jpg",
	led: "/image/gykj/categories/led-therapy.jpg",
	massager: "/image/gykj/categories/butterfly-massager.jpg",
	massage: "/image/gykj/categories/butterfly-massager.jpg",
};

function getImage(cat: CategoryEdge): string {
	if (cat.image) return cat.image;

	const rawMeta = cat.node.metadata?.find((m) => m.key === "media")?.value;
	if (rawMeta) {
		try {
			const parsed = JSON.parse(rawMeta) as { url?: string }[];
			const url = parsed[0]?.url;
			if (url) return url;
		} catch {
			// ignore malformed metadata
		}
	}

	const combined = `${cat.node.name} ${cat.node.slug}`.toLowerCase();
	for (const [kw, path] of Object.entries(IMAGE_MAP)) {
		if (combined.includes(kw)) return path;
	}
	return "/image/gykj/categories/cavitation-40k.jpg";
}

export default function CategoryDiscovery({ locale }: { locale: string }) {
	const t = useTranslations("homeBeauty");
	const [cats, setCats] = useState<CategoryEdge[]>([]);
	const scrollRef = useRef<HTMLDivElement>(null);
	const { channel } = useChannel(locale);

	useEffect(() => {
		(async () => {
			if (!channel) return;
			try {
				const data = await executeGraphQL(ProductCategoriesListDocument, {
					withAuth: false,
					variables: { channel, locale: handleGraphqlLocale(locale || defaultLocale), first: 30 },
					revalidate: 0,
				});
				if (data?.categories?.edges) setCats(data.categories.edges as CategoryEdge[]);
			} catch (err) {
				console.error("[CategoryDiscovery]", err);
			}
		})();
	}, [locale, channel]);

	const items = cats.slice(0, 6);

	return (
		<section className="w-full bg-white py-16 sm:py-20 md:py-24">
			<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
				<header className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
					<div>
						<span className="font-body text-brand-fg inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.2em] xl:text-[13px]">
							<span aria-hidden className="bg-brand inline-block h-px w-8" />
							{t("categoryEyebrow")}
						</span>
						<h2 className="font-display text-brand-ink mt-4 text-[clamp(32px,3.6vw,60px)] leading-[1.1]">
							{t("categoryTitle")}
						</h2>
					</div>
					<Link
						href="/products"
						className="border-brand-ink/42 font-body text-brand-ink hover:border-brand hover:!text-brand-fg group inline-flex items-center gap-2 border-b pb-1 text-[13px] font-semibold uppercase tracking-[0.22em] transition-colors duration-200 xl:text-[14px]"
					>
						{t("categoryViewAll")}
						<span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">
							→
						</span>
					</Link>
				</header>

				{items.length > 0 ? (
					<div
						ref={scrollRef}
						className="scrollbar-none mt-10 flex gap-4 overflow-x-auto pb-4 md:mt-14 md:grid md:grid-cols-4 md:gap-5 md:overflow-visible md:pb-0 lg:grid-cols-6"
					>
						{items.map((cat, i) => {
							const displayName =
								locale === defaultLocale
									? cat.node.name
									: cat.node.translation?.name || cat.node.name;
							return (
							<Link
								key={cat.node.id}
								href={`/products/${cat.node.slug}`}
								className="group relative flex w-[160px] shrink-0 flex-col overflow-hidden md:w-auto"
							>
								<div className="bg-brand-softer relative aspect-[3/4] w-full overflow-hidden">
									<Image
										src={getImage(cat)}
										alt={displayName}
										fill
										sizes="(max-width:768px) 160px, (max-width:1024px) 25vw, 14vw"
										className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
									/>
									<div
										className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 via-40% to-transparent"
										aria-hidden
									/>
									<div className="absolute inset-x-0 bottom-0 p-4">
										<h3
											className="font-display text-[20px] leading-snug text-white md:text-[22px] lg:text-[24px]"
											style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5), 0 0 12px rgba(0,0,0,0.25)" }}
										>
											{displayName}
										</h3>
										<span
											className="font-body mt-1.5 inline-block text-[12px] font-medium uppercase tracking-[0.14em] text-white/95 sm:text-[13px]"
											style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
										>
											{(cat.node.products?.totalCount ?? 0) || "6K+"} Products
										</span>
									</div>
								</div>
							</Link>
							);
						})}
					</div>
				) : (
					<div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
						{Array.from({ length: 6 }).map((_, i) => (
							<div key={i} className="bg-brand-softer/40 aspect-[3/4] animate-pulse" />
						))}
					</div>
				)}
			</div>
		</section>
	);
}
